import type React from 'react';

import { startCase } from 'lodash-es';
import qs from 'qs';

import { Button } from '@change/ds-core-ui/Button';
import { PetitionCoverCard } from '@change/ds-core-ui/PetitionCoverCard';
import { ArrowRightIcon } from '@change/ds-icons/ArrowRightIcon';

import { useI18n } from '@change-corgi/core-react/i18n';

import { isError, isLoaded } from '#src/app/shared/utils/async';

import { TrendingSectionSkeleton } from '../skeletons';

import { SeeAllTopicsButton } from './components/SeeAllTopicsButton';
import { TrendingSectionHeader } from './components/TrendingSectionHeader';
import { useTrendingSection } from './hooks/useTrendingSection';

export const TrendingSection: React.FC<EmptyProps> = () => {
	const {
		actions,
		data: { trendingData },
	} = useTrendingSection();

	const { translatePlural } = useI18n();

	if (isError(trendingData)) return null;
	if (!isLoaded(trendingData)) return <TrendingSectionSkeleton />;

	const { shouldShowSection, petitions, topics } = trendingData.value;

	if (!shouldShowSection) return null;

	return (
		<section
			className="text-typography-strong grid gap-6 md:grid-cols-[1fr_auto]"
			aria-labelledby="trending-section-title"
			data-testid="homepage-trending-section"
		>
			<TrendingSectionHeader />

			{/* auto-placed into the top-right grid cell; wrapper divs own the breakpoint
			    visibility so the Button keeps its own display type */}
			<div className="hidden md:block md:self-center">
				<SeeAllTopicsButton device="desktop" onClick={actions.onAllTopicsClick} />
			</div>

			<div className="flex flex-wrap items-center gap-2 md:col-span-2" data-qa="featured-topics">
				{topics.map(({ name, slug }) => (
					<Button
						data-testid="topic-chip"
						data-qa="topic-chip"
						key={name}
						as="a"
						href={`/topic/${encodeURIComponent(slug)}?${qs.stringify({ source_location: 'homepage' })}`}
						variant="secondary"
						size="small"
						trailingIcon={<ArrowRightIcon />}
						onClick={() => actions.onTopicClick(name)}
					>
						{startCase(name)}
					</Button>
				))}
			</div>
			<div className="flex flex-col items-center gap-6 md:col-span-2">
				<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
					{petitions.map(({ node: petition }, index) => (
						<PetitionCoverCard
							averageColor={petition.photo?.petitionSmall.palette?.averageColor}
							key={petition.id}
							imageUrl={petition.photo?.petitionLarge.url}
							petitionId={petition.id}
							petitionLinkOnClick={() =>
								actions.onPetitionCardClick({
									ask: petition.displayTitle,
									petitionId: petition.id,
									signatureCount: petition.signatureState.signatureCount.displayed,
									tags: petition.tagsConnection?.nodes ?? [],
									cardPosition: index + 1,
									location: petition.relevantLocationLocalizedName ?? '',
								})
							}
							petitionLinkUrl={`/p/${encodeURIComponent(petition.slug)}?${qs.stringify({ source_location: 'homepage' })}`}
							signaturesLabel={translatePlural(
								'corgi.components.petition_card.signatures',
								petition.signatureState.signatureCount.displayed,
								{
									countLocalized: petition.signatureState.signatureCount.displayedLocalized,
								},
							)}
							title={petition.displayTitle}
							data-qa="petition-card"
							data-testid={`trending-section-petition-card-${petition.id}`}
						/>
					))}
				</div>
			</div>

			<div className="md:hidden">
				<SeeAllTopicsButton device="mobile" onClick={actions.onAllTopicsClick} />
			</div>
		</section>
	);
};
